/**
 * FAQs ([faqs] shortcode): AJAX industry filter, search and "Load more", plus animated <details> accordions.
 */
const DURATION = 300;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SEARCH_DELAY = 350;

const running = new WeakMap();
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Open/close a FAQ with a height + fade animation.
 * The .is-open class flips immediately (for the icon); [open] is only removed once the close animation ends.
 */
function toggleFaq(details) {
  const answer = details.querySelector('.dmn-faq__answer');
  const opening = !details.classList.contains('is-open');

  details.classList.toggle('is-open', opening);

  if (!answer || !answer.animate || prefersReducedMotion()) {
    details.open = opening;
    return;
  }

  // Reversing mid-animation: start from where it currently is.
  const current = running.get(details);
  const fromHeight = current ? answer.getBoundingClientRect().height : opening ? 0 : answer.offsetHeight;
  const fromOpacity = current ? getComputedStyle(answer).opacity : opening ? 0 : 1;
  current?.cancel();

  if (opening) details.open = true;
  const toHeight = opening ? answer.scrollHeight : 0;

  answer.style.overflow = 'hidden';
  const animation = answer.animate(
    [
      { height: `${fromHeight}px`, opacity: fromOpacity },
      { height: `${toHeight}px`, opacity: opening ? 1 : 0 },
    ],
    { duration: DURATION, easing: EASING }
  );

  running.set(details, animation);
  animation.onfinish = () => {
    running.delete(details);
    answer.style.overflow = '';
    if (!opening) details.open = false;
  };
}

document.addEventListener('click', (e) => {
  const summary = e.target.closest('.dmn-faq > summary');
  if (!summary) return;

  e.preventDefault();
  toggleFaq(summary.parentElement);
});

// Keep .is-open in sync when the browser opens a <details> itself (e.g. find-in-page).
document.addEventListener(
  'toggle',
  (e) => {
    const details = e.target;
    if (details.classList?.contains('dmn-faq') && !running.has(details)) {
      details.classList.toggle('is-open', details.open);
    }
  },
  true
);

/**
 * Deal new FAQs into the two columns, left/right in turn, continuing from what's already there
 * (the server renders the first page the same way).
 */
function addToColumns(columns, html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const items = [...template.content.querySelectorAll('.dmn-faq')];
  let count = columns.reduce((total, column) => total + column.children.length, 0);

  items.forEach((item) => columns[count++ % columns.length].append(item));
  return items;
}

function setupFaqs(el) {
  const form = el.querySelector('.dmn-faqs__filters');
  const industry = el.querySelector('.dmn-faqs__industry');
  const search = el.querySelector('.dmn-faqs__search-input');
  const searchButton = el.querySelector('.dmn-faqs__search-button');
  const clearButton = el.querySelector('.dmn-faqs__clear-button');
  const columns = [...el.querySelectorAll('.dmn-faqs__column')];
  const more = el.querySelector('.dmn-faqs__more');
  const empty = el.querySelector('.dmn-faqs__empty');
  const status = el.querySelector('.dmn-faqs__status');

  let page = 1;
  let controller = null;
  let timer = null;

  async function load(append = false) {
    controller?.abort();
    const request = (controller = new AbortController());

    const url = new URL(el.dataset.endpoint, window.location.href);
    url.searchParams.set('page', append ? page + 1 : 1);
    url.searchParams.set('per_page', el.dataset.perPage);
    url.searchParams.set('industry', industry ? industry.value : '');
    url.searchParams.set('search', search ? search.value.trim() : '');

    el.classList.add('is-loading');
    el.setAttribute('aria-busy', 'true');
    more.disabled = true;

    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (!append) columns.forEach((column) => column.replaceChildren());
      const added = addToColumns(columns, data.html);
      // Move focus to the first new question so keyboard users continue from there.
      if (append) added[0]?.querySelector('summary')?.focus();

      page = data.page;
      empty.hidden = data.total > 0;
      more.hidden = page >= data.max_pages;
      status.textContent = el.dataset.foundText.replace('%d', data.total);
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = el.dataset.errorText;
      console.error('FAQs:', error);
    } finally {
      // A newer request may have replaced this one; leave the loading state to it.
      if (controller === request) {
        el.classList.remove('is-loading');
        el.removeAttribute('aria-busy');
        more.disabled = false;
      }
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    clearTimeout(timer);
    load();
  });

  industry?.addEventListener('change', () => {
    clearTimeout(timer);
    load();
  });

  // Show the X instead of the magnifier while there's text to clear.
  const syncSearchButtons = () => {
    const hasValue = search.value !== '';
    searchButton.hidden = hasValue;
    clearButton.hidden = !hasValue;
  };

  search?.addEventListener('input', () => {
    syncSearchButtons();
    clearTimeout(timer);
    timer = setTimeout(load, SEARCH_DELAY);
  });

  clearButton?.addEventListener('click', () => {
    search.value = '';
    syncSearchButtons();
    clearTimeout(timer);
    load();
    // The X disappears, so keep focus in the field rather than losing it.
    search.focus();
  });

  if (search && searchButton && clearButton) syncSearchButtons();

  more.addEventListener('click', () => load(true));
}

export function initFaqs(root = document) {
  root.querySelectorAll('.dmn-faqs').forEach((el) => {
    if (el.dataset.ready) return;
    el.dataset.ready = '1';
    setupFaqs(el);
  });
}
